import registrationAPI from '../api/registrationAPI';
import { http } from '../api/http';

// Per-tab basket isolation: each tab keeps a unique token in sessionStorage
// (scoped to the tab — survives a reload, isolated from other tabs) and threads
// it on every request, so two registration tabs never clobber each other's
// basket. More reliable than the old form's session-pointer approach, which
// concurrent tabs in the same browser would still share.
const tokenKey = (pathname) => `reg_basket_token_${pathname}`;

function generateBasketToken() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function readStoredToken(pathname) {
  try { return window.sessionStorage.getItem(tokenKey(pathname)); } catch (e) { return null; }
}

function storeToken(pathname, token) {
  try { window.sessionStorage.setItem(tokenKey(pathname), token); } catch (e) { /* private mode */ }
}

// Server-state store for the registration page. Mirrors the old modal's
// basket.module.js responsibilities (basket-token threading, coupon/WA
// refetch, free-basket short-circuit) but against the new JSON API and with
// the prototype's step model.

const blankBasket = () => ({
  token: null,
  count_tickets: 0,
  is_single_ticket: false,
  is_agenda_group: false,
  donation: 0,
  order: { tickets: [], history: [], summary: {} },
});

export default {
  namespaced: true,

  state: () => ({
    pathname: '',
    basketToken: '',
    isFetching: false,
    showIndicator: false,
    errorMessage: '',

    event: {},
    ceInfo: { show: false },
    settings: {},
    ticketCategories: [],
    tickets: { inperson: [], virtual: [] },
    basket: blankBasket(),

    attendees: {
      fields: [],
      validation: {},
      list: [],
      addons: { total_session_addons: 0, total_item_addons: 0 },
      membershipDiscount: false,
      fieldsWithCoupons: [],
    },

    sessions: {
      enabled: false,
      concurrentSession: false,
      selectedSessionIds: [],
      days: [],
    },

    paymentInfo: null,
    agenda: null,
    faculty: null,
  }),

  getters: {
    currencySymbol: (state) => state.settings.currency_symbol || '$',
    order: (state) => state.basket.order || {},
    orderSummary: (state) => (state.basket.order && state.basket.order.summary) || {},
    orderHistory: (state) => (state.basket.order && state.basket.order.history) || [],
    totalPrice: (state, getters) => Number(getters.orderSummary.total_price || 0),
    settings: (state) => state.settings,
    // Sessions step exists only for a single in-person ticket with agenda groups.
    hasSessionsStep: (state) => state.basket.is_agenda_group,
    isFree: (state, getters) => getters.totalPrice <= 0,
  },

  mutations: {
    setPathname: (state, pathname) => { state.pathname = pathname; },
    setBasketToken: (state, token) => { state.basketToken = token || ''; },
    setFetching: (state, value) => { state.isFetching = value; },
    setIndicator: (state, value) => { state.showIndicator = value; },
    setError: (state, message) => { state.errorMessage = message || ''; },

    setBootstrap(state, data) {
      state.event = data.event || {};
      state.ceInfo = data.ce_info || { show: false };
      state.settings = data.settings || {};
      state.ticketCategories = data.ticket_categories || [];
      state.tickets = data.tickets || { inperson: [], virtual: [] };
      state.basket = data.basket || blankBasket();
    },

    setTickets: (state, tickets) => { if (tickets) state.tickets = tickets; },
    setBasket: (state, basket) => { if (basket) state.basket = basket; },

    setAttendees(state, data) {
      state.attendees = {
        fields: data.form ? data.form.fields : [],
        validation: data.validation || {},
        list: data.attendees || [],
        addons: data.addons || { total_session_addons: 0, total_item_addons: 0 },
        membershipDiscount: !!data.membership_discount,
        fieldsWithCoupons: data.fields_with_coupons || [],
      };
      if (data.basket) state.basket = data.basket;
    },

    setMembershipDiscount: (state, value) => { state.attendees.membershipDiscount = !!value; },

    setSessions(state, data) {
      state.sessions = {
        enabled: !!data.enabled,
        concurrentSession: !!data.concurrent_session,
        selectedSessionIds: data.selected_session_ids || [],
        days: data.days || [],
      };
    },

    setPaymentInfo: (state, info) => { state.paymentInfo = info; },
    setAgenda: (state, agenda) => { state.agenda = agenda; },
    setFaculty: (state, faculty) => { state.faculty = faculty; },
  },

  actions: {
    init({ commit }, { pathname }) {
      commit('setPathname', pathname);

      // Reuse this tab's token across reloads; mint a fresh one for a new tab.
      let token = readStoredToken(pathname);
      if (!token) {
        token = generateBasketToken();
        storeToken(pathname, token);
      }
      commit('setBasketToken', token);
      http.setBasketToken(token);
    },

    // Adopt a token that arrived out-of-band (e.g. a payment-retry redirect
    // carrying ?basket_token=), so this tab resumes that exact basket.
    useBasketToken({ commit, state }, token) {
      if (!token) {
        return;
      }
      commit('setBasketToken', token);
      storeToken(state.pathname, token);
      http.setBasketToken(token);
    },

    async bootstrap({ commit, state }, params = {}) {
      commit('setFetching', true);
      commit('setError', '');
      try {
        const data = await registrationAPI.bootstrap(state.pathname, params);
        commit('setBootstrap', data);
        return data;
      } catch (e) {
        commit('setError', e.message);
        throw e;
      } finally {
        commit('setFetching', false);
      }
    },

    async updateBasket({ commit, state }, payload) {
      commit('setIndicator', true);
      commit('setError', '');
      try {
        const data = await registrationAPI.updateBasket(state.pathname, payload);
        commit('setTickets', data.tickets);
        commit('setBasket', data.basket);
        return data;
      } catch (e) {
        commit('setError', e.message);
        throw e;
      } finally {
        commit('setIndicator', false);
      }
    },

    async clearBasket({ commit, state }) {
      const data = await registrationAPI.clearBasket(state.pathname);
      commit('setTickets', data.tickets);
      commit('setBasket', data.basket);
      return data;
    },

    async applyCoupon({ commit, state }, code) {
      commit('setError', '');
      try {
        const data = await registrationAPI.applyCoupon(state.pathname, { code });
        commit('setTickets', data.tickets);
        commit('setBasket', data.basket);
        return data;
      } catch (e) {
        // Surfaced inline by the coupon input; don't set the global banner.
        throw e;
      }
    },

    async fetchAttendees({ commit, state }) {
      commit('setFetching', true);
      try {
        const data = await registrationAPI.fetchAttendees(state.pathname);
        commit('setAttendees', data);
        return data;
      } finally {
        commit('setFetching', false);
      }
    },

    async updateAttendees({ commit, state }, payload) {
      commit('setIndicator', true);
      commit('setError', '');
      try {
        const data = await registrationAPI.updateAttendees(state.pathname, payload);
        commit('setAttendees', data);
        return data;
      } finally {
        commit('setIndicator', false);
      }
    },

    async checkField({ commit, state }, payload) {
      const data = await registrationAPI.checkField(state.pathname, payload);
      commit('setTickets', data.tickets);
      commit('setBasket', data.basket);
      return data;
    },

    async checkMembershipEmail({ commit, state }, payload) {
      const data = await registrationAPI.checkMembershipEmail(state.pathname, payload);
      commit('setTickets', data.tickets);
      commit('setBasket', data.basket);
      commit('setMembershipDiscount', data.membership_discount);
      return data;
    },

    async fetchSessions({ commit, state }) {
      const data = await registrationAPI.fetchSessions(state.pathname);
      commit('setSessions', data);
      return data;
    },

    async updateSessions({ state }, sessionIds) {
      return registrationAPI.updateSessions(state.pathname, { sessions: sessionIds });
    },

    async preparePayment({ commit, state }, payload = {}) {
      commit('setIndicator', true);
      commit('setError', '');
      try {
        const info = await registrationAPI.preparePayment(state.pathname, payload);
        commit('setPaymentInfo', info);
        return info;
      } finally {
        commit('setIndicator', false);
      }
    },

    async confirmPayment({ state }, params = {}) {
      return registrationAPI.confirmPayment(state.pathname, params);
    },

    async fetchAgenda({ commit, state }) {
      if (state.agenda) return state.agenda;
      const data = await registrationAPI.fetchAgenda(state.pathname);
      commit('setAgenda', data);
      return data;
    },

    async fetchFaculty({ commit, state }) {
      if (state.faculty) return state.faculty;
      const data = await registrationAPI.fetchFaculty(state.pathname);
      commit('setFaculty', data);
      return data;
    },
  },
};
